'use client';

import { useEffect, useState } from "react";
import { MDXRemote } from "next-mdx-remote";
import type { MDXRemoteSerializeResult } from "next-mdx-remote";
import { FiTerminal, FiDatabase, FiLayers, FiActivity, FiEye } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

// Components
import ReadingProgress from "@/components/blog/ReadingProgress";
import ScrollToTop from "@/components/blog/ScrollToTop";
import TableOfContents from "@/components/blog/TableOfContents";
import MobileTOC from "@/components/blog/MobileTOC";
import BlogReactions from "@/components/blog/BlogReactions";
import ShareButtons from "@/components/share/ShareButtons";
import BlogCard from "@/components/cards/BlogCard";
import MDXComponents from "@/components/mdx/MDXComponents";
import NewsletterSignup from "@/components/blog/NewsletterSignup";

// Actions & Types
import { FormattedBlogPost, Heading } from "@/lib/blog/blog-utils";
import { incrementViews } from "@/app/actions/views";

interface Props {
  post: FormattedBlogPost;
  rawMdx: string;
  headings: Heading[];
  relatedPosts: FormattedBlogPost[];
}

export default function BlogDetailClient({
  post,
  rawMdx,
  headings,
  relatedPosts,
}: Props) {
  const [mdxSource, setMdxSource] = useState<MDXRemoteSerializeResult | null>(null);
  const [error, setError] = useState(false);
  const [viewCount, setViewCount] = useState(post.views || 0);

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || "";
  const ogImageUrl = `${siteUrl}/api/og/${post.slug}`;

  useEffect(() => {
    let cancelled = false;

    // 1. Sync Telemetry (Views)
    const syncTelemetry = async () => {
      await incrementViews(post.slug);
      if (!cancelled) {
        setViewCount((prev) => prev + 1); // Optimistic UI update
      }
    };

    // 2. Load and Decrypt MDX
    async function loadMdx() {
      try {
        const res = await fetch("/api/mdx", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: rawMdx }),
        });
        if (!res.ok) throw new Error("MDX fetch failed");
        const data = await res.json();
        if (!cancelled) setMdxSource(data);
      } catch (err) {
        console.error("Failed to load MDX:", err);
        if (!cancelled) setError(true);
      }
    }

    syncTelemetry();
    loadMdx();

    return () => {
      cancelled = true;
    };
  }, [post.slug, rawMdx]); // Added post.slug to trigger on navigation

  if (error) {
    return (
      <div className="py-48 text-center flex flex-col items-center justify-center">
        <div className="p-6 bg-red-500/10 rounded-full mb-6">
          <FiTerminal className="text-red-500 text-4xl animate-pulse" />
        </div>
        <h2 className="text-2xl font-black uppercase tracking-tighter mb-2">Transmission Corrupted</h2>
        <p className="text-zinc-500 font-mono text-xs uppercase tracking-[0.3em]">Error_Code: 0x000F4E_MDX_FAIL</p>
      </div>
    );
  }

  return (
    <>
      <ReadingProgress />
      <ScrollToTop />
      <MobileTOC headings={headings} />

      <div className="max-w-[1440px] mx-auto flex flex-col xl:flex-row gap-16 px-6 sm:px-12 py-12 lg:py-24">
        
        {/* LEFT: Sticky Sidebar Navigation */}
        <aside className="hidden xl:block w-72 sticky top-32 self-start">
          <div className="space-y-12">
            <div className="pl-4 border-l-2 border-zinc-100 dark:border-zinc-800">
              <h3 className="text-[10px] font-black uppercase tracking-[0.4em] text-zinc-400 mb-8 flex items-center gap-2">
                <FiLayers className="text-indigo-500" /> Index_Nodes
              </h3>
              <TableOfContents headings={headings} />
            </div>
            
            {/* META TELEMETRY BOX */}
            <div className="p-6 rounded-[2rem] bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-100 dark:border-zinc-800 backdrop-blur-md">
               <h4 className="text-[10px] font-black uppercase tracking-widest mb-6 flex items-center gap-2 text-zinc-800 dark:text-zinc-200">
                 <FiDatabase className="text-indigo-500" /> Meta_Telemetry
               </h4>
               <div className="space-y-4">
                 {[
                   { label: 'Status', value: 'Verified', color: 'text-emerald-500' },
                   { label: 'Telemetry', value: `${viewCount.toLocaleString()} VISITS`, color: 'text-indigo-500' },
                   { label: 'Category', value: post.category ?? 'General', color: 'text-indigo-500' },
                   { label: 'Integrity', value: 'SHA-256', color: 'text-zinc-500' },
                 ].map((stat) => (
                   <div key={stat.label} className="flex justify-between items-center text-[10px] font-bold">
                     <span className="text-zinc-400 uppercase tracking-tighter">{stat.label}</span>
                     <span className={`${stat.color} font-mono uppercase tracking-widest`}>{stat.value}</span>
                   </div>
                 ))}
               </div>
            </div>
          </div>
        </aside>

        {/* CENTER: Main Article */}
        <main className="flex-1 max-w-3xl min-w-0">
          <header className="mb-20 space-y-8">
            <div className="flex items-center gap-4">
              <span className="h-px w-12 bg-indigo-500" />
              <span className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-500">
                Log_Entry_{new Date(post.publishedAt).getFullYear()}
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight text-zinc-900 dark:text-white leading-[0.85] uppercase italic">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 border-t border-zinc-100 dark:border-zinc-900 pt-8">
               <div className="flex -space-x-2">
                 <div className="w-8 h-8 rounded-full border-2 border-white dark:border-zinc-950 bg-indigo-500 flex items-center justify-center text-[10px] font-black text-white shadow-lg">EA</div>
               </div>
               <div className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-500 space-x-4 flex items-center flex-wrap">
                 <span className="flex items-center gap-1.5"><FiEye className="text-indigo-500" /> {viewCount.toLocaleString()} Visits</span>
                 <span className="text-zinc-300 dark:text-zinc-800">/</span>
                 <span>{post.stats.minutes}m Read</span>
                 <span className="text-zinc-300 dark:text-zinc-800">/</span>
                 <span>{post.stats.words} Words</span>
               </div>
            </div>
          </header>

          {/* Content Section: Techy Loader + Clean MDX */}
          <AnimatePresence mode="wait">
            {!mdxSource ? (
              <motion.div 
                key="loader"
                exit={{ opacity: 0 }}
                className="py-32 flex flex-col items-center gap-6 border-2 border-dashed border-zinc-100 dark:border-zinc-900 rounded-[3rem] bg-zinc-50/50 dark:bg-zinc-900/20"
              >
                <div className="flex gap-2">
                  {/* Animated bars with subtle wave */}
                  <motion.div 
                    animate={{ height: [16, 32, 16] }} 
                    transition={{ repeat: Infinity, duration: 0.6, ease: "easeInOut", delay: 0 }} 
                    className="w-1.5 bg-indigo-500 rounded-full" 
                  />
                  <motion.div 
                    animate={{ height: [32, 16, 32] }} 
                    transition={{ repeat: Infinity, duration: 0.6, ease: "easeInOut", delay: 0.2 }} 
                    className="w-1.5 bg-indigo-500 rounded-full" 
                  />
                  <motion.div 
                    animate={{ height: [16, 32, 16] }} 
                    transition={{ repeat: Infinity, duration: 0.6, ease: "easeInOut", delay: 0.4 }} 
                    className="w-1.5 bg-indigo-500 rounded-full" 
                  />
                </div>

                {/* Loader text */}
                <p className="text-[10px] font-mono font-black uppercase tracking-[0.5em] text-zinc-400 animate-pulse">
                  Decrypting_Signal...
                </p>
              </motion.div>
            ) : (
              <motion.div 
                key="content"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="
                  prose prose-zinc dark:prose-invert max-w-none

                  /* Body Styling - Clean & readable */
                  prose-p:text-lg prose-p:leading-relaxed prose-p:mb-8
                  prose-p:text-zinc-600 dark:prose-p:text-zinc-400

                  /* Images - Rounded, subtle spacing */
                  prose-img:rounded-xl prose-img:my-12 prose-img:first-of-type:mt-0

                  /* Blockquotes - Subtle, modern */
                  prose-blockquote:border-l-4 prose-blockquote:border-indigo-500 
                  prose-blockquote:bg-zinc-50 dark:prose-blockquote:bg-zinc-900/40 
                  prose-blockquote:p-6 prose-blockquote:rounded-r-xl prose-blockquote:not-italic

                  /* Headers - Tight & bold */
                  prose-headings:tracking-tight prose-headings:font-black
                "
              >
                <MDXRemote {...mdxSource} components={MDXComponents} />
              </motion.div>
            )}
          </AnimatePresence>


          <div className="mt-32">
            <NewsletterSignup />
          </div>

          <footer className="mt-20 border-t border-zinc-100 dark:border-zinc-900 pt-16 flex flex-col md:flex-row items-center justify-between gap-12">
            <BlogReactions slug={post.slug} />
            <ShareButtons post={post} ogImageUrl={ogImageUrl} />
          </footer>
        </main>

        {/* RIGHT: Visual Activity Monitor */}
        <aside className="hidden 2xl:block w-48 sticky top-32 self-start space-y-12 opacity-40 hover:opacity-100 transition-opacity">
           <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-[9px] font-mono uppercase tracking-[0.2em]">Activity_Log</span>
                <span className="text-[8px] font-mono text-indigo-500 animate-pulse">LIVE</span>
              </div>
              <div className="flex gap-0.5 h-10 items-end border-b border-zinc-100 dark:border-zinc-800 pb-1">
                {[40, 70, 45, 90, 65, 80, 30, 95, 50, 85].map((h, i) => (
                  <motion.div 
                    key={i} 
                    initial={{ height: 0 }}
                    animate={{ height: `${h}%` }}
                    className="flex-1 bg-indigo-500/30" 
                  />
                ))}
              </div>
           </div>
           <div className="text-[9px] font-mono leading-relaxed uppercase tracking-tighter text-zinc-500">
             Analyzing document structure... <br />
             <span className="text-indigo-500">Node_ID: {post.slug.slice(0, 8).toUpperCase()}</span>
             <br />
             <span className="mt-2 block italic text-[8px]">Scanning for semantic patterns...</span>
           </div>
        </aside>
      </div>

      {/* CROSS REFERENCE SECTION */}
      {relatedPosts.length > 0 && (
        <section className="relative py-40 bg-zinc-950 overflow-hidden mt-20">
          <div className="absolute inset-0 opacity-10 pointer-events-none" 
               style={{ backgroundImage: 'radial-gradient(circle, #4f46e5 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="flex flex-col items-center mb-24 space-y-6 text-center">
              <div className="p-3 bg-indigo-500/10 rounded-2xl border border-indigo-500/20">
                <FiActivity className="text-indigo-500 animate-pulse" />
              </div>
              <h2 className="text-5xl md:text-7xl font-black tracking-tighter uppercase text-white leading-none">
                Intelligence <br /> <span className="text-zinc-800">Cross_Reference</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedPosts.map((p) => (
                <div key={p.slug} className="group">
                  <BlogCard post={p} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}